import { resolveApiOrigin } from '../../api/api-origin.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

/**
 * SharedPresentationViewer — public viewer page.
 *
 * Mounted at `/p/:id/:token`, OUTSIDE the Sage app chrome and auth
 * bootstrap (see main.tsx). Anyone with the link can load it — the
 * share token gates access. Renders the deck inside an iframe sourced
 * from the public kiosk endpoint
 * (`/reporting/api/kiosk/presentations/:id/view?token=`), which serves
 * a full self-contained HTML document.
 *
 * Mirrors SharedReportViewer; the presentation kiosk route differs only
 * in that it needs both the presentation id AND the plaintext token
 * (the report kiosk keyed on the token alone).
 *
 * On a missing / expired / revoked token the kiosk endpoint returns a
 * JSON 4xx; the iframe shows the browser's default error for that, so
 * we also surface a friendly message in the SPA layer.
 */
export function SharedPresentationViewer(): JSX.Element {
  const { id, token } = useParams<{ id: string; token: string }>();
  const [iframeKey, setIframeKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  function handleFullscreen(): void {
    const el = iframeRef.current as
      | (HTMLIFrameElement & {
          webkitRequestFullscreen?: () => Promise<void>;
        })
      | null;
    if (!el) return;
    const req = el.requestFullscreen?.bind(el) ?? el.webkitRequestFullscreen?.bind(el);
    if (!req) return;
    void req().catch(() => {
      /* user-denied or unsupported */
    });
  }

  const kioskUrl = useMemo(() => {
    if (!id || !token) return '';
    const apiOrigin = resolveApiOrigin().replace(/\/+$/u, '');
    return `${apiOrigin}/reporting/api/kiosk/presentations/${encodeURIComponent(id)}/view?token=${encodeURIComponent(token)}`;
  }, [id, token]);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Shared presentation';
    return () => {
      document.title = prev;
    };
  }, []);

  if (!id || !token) {
    return <ViewerError message="Invalid share link." />;
  }

  return (
    <div className="shared-viewer">
      {loading && (
        <div className="shared-viewer-loading" aria-live="polite">
          Loading…
        </div>
      )}
      {error && <ViewerError message={error} />}
      <iframe
        key={iframeKey}
        ref={iframeRef}
        title="Shared presentation"
        src={kioskUrl}
        className="shared-viewer-iframe"
        // Allow the deck's own inline scripts (keyboard nav, slide
        // transitions) but block top-level navigation / form posts.
        // The token already gates access — this is defense-in-depth.
        sandbox="allow-scripts allow-same-origin allow-modals"
        onLoad={() => setLoading(false)}
        onError={() => {
          setLoading(false);
          setError('Failed to load presentation.');
        }}
      />
      <ViewerStyles />
      <div className="shared-viewer-actions">
        <button
          type="button"
          className="shared-viewer-fab"
          onClick={handleFullscreen}
          title="Present full-screen (Esc to exit)"
          aria-label="Present full-screen"
        >
          ⛶
        </button>
        <button
          type="button"
          className="shared-viewer-fab"
          onClick={() => {
            setLoading(true);
            setError(null);
            setIframeKey((k) => k + 1);
          }}
          title="Reload"
          aria-label="Reload"
        >
          ↻
        </button>
      </div>
    </div>
  );
}

function ViewerError({ message }: { message: string }): JSX.Element {
  return (
    <div className="shared-viewer-error">
      <h1>Presentation unavailable</h1>
      <p>{message}</p>
    </div>
  );
}

function ViewerStyles(): JSX.Element {
  return (
    <style>{`
      html, body, #root { margin:0; padding:0; height:100%; background:#0a0b0e; }
      .shared-viewer{ position:fixed; inset:0; background:#fff; }
      .shared-viewer-iframe{
        position:absolute; inset:0; width:100%; height:100%;
        border:0; background:#fff;
      }
      .shared-viewer-loading{
        position:absolute; top:50%; left:50%; transform:translate(-50%,-50%);
        font: 500 14px/1.2 ui-sans-serif,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;
        color:#697184; background:#fff; padding:8px 14px; border-radius:6px;
        box-shadow:0 2px 8px rgba(0,0,0,0.06);
      }
      .shared-viewer-error{
        position:absolute; top:0; left:0; right:0; bottom:0;
        display:flex; flex-direction:column; align-items:center; justify-content:center;
        background:#fff; color:#0b1220; padding:32px;
        font-family: ui-sans-serif,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;
      }
      .shared-viewer-error h1{ font-size:20px; margin:0 0 8px 0; }
      .shared-viewer-error p{ margin:0; color:#697184; }
      .shared-viewer-actions{
        position:fixed; bottom:14px; right:14px; z-index:50;
        display:flex; gap:8px;
      }
      .shared-viewer-fab{
        appearance:none; border:none; cursor:pointer;
        background:rgba(15,23,42,0.85); color:#fff;
        width:36px; height:36px; border-radius:50%;
        font-size:18px; line-height:1;
        box-shadow:0 4px 12px rgba(0,0,0,0.18);
        display:inline-flex; align-items:center; justify-content:center;
      }
      .shared-viewer-fab:hover{ background:rgba(15,23,42,1); }
      /* Hide our chrome inside the fullscreen iframe itself */
      .shared-viewer-iframe:fullscreen{ background:#000; }
      @media print { .shared-viewer-actions, .shared-viewer-loading { display:none !important; } }
    `}</style>
  );
}
